import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { frFR, enUS } from '@mui/x-data-grid/locales';
import { useTranslation } from 'react-i18next';
import { GlobalStyles, useTheme, useMediaQuery } from '@mui/material';

const ReportsPage = () => {
    const { t, i18n } = useTranslation();
    const [reportType, setReportType] = useState('overview');

    // For Table Thead Width
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isLarge = useMediaQuery(theme.breakpoints.up('sm'));

    const reportModules = [
        { key: 'overview', label: t('overview', 'Overview') },
        { key: 'user_reports', label: t('user_reports', 'User Reports') },
        { key: 'revenue_reports', label: t('revenue_reports', 'Revenue Reports') },
        { key: 'transaction_logs', label: t('transaction_logs', 'Transaction Logs') },
        { key: 'system_logs', label: t('system_logs', 'System Logs') },
    ];

    const currentLabel = reportModules.find(m => m.key === reportType)?.label || reportType;

    return (
        <Box sx={{ p: '36px 24px 24px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: '24px', flexWrap: 'wrap', columnGap: '6px', rowGap: '16px' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px' }} className='main_title'>{t('reports_analytics', 'Reports & Analytics')}</Typography>
                <Button 
                    variant="contained" 
                    sx={{ 
                        border: '1px solid rgba(230, 210, 249, 0.1) !important',
                        color: '#fff',
                        textTransform: 'none',
                        backgroundColor: '#FF69B4 !important',
                        borderRadius: '999px',
                        fontSize: '15px',
                        lineHeight: '24px',
                        padding: '6px 20px',
                        minWidth: '38px',
                        '& .MuiButton-startIcon': {
                            margin: 0,
                        },
                    }}
                >{t('export_pdf', 'Export PDF')}</Button>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                    <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px', padding: '16px !important' }}>
                        <CardContent sx={{ padding: '0 !important' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {reportModules.map((module) => (
                                    <Button
                                        key={module.key}
                                        onClick={() => setReportType(module.key)}
                                        variant={reportType === module.key ? "contained" : "text"}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            padding: '8px 20px',
                                            minHeight: '42px',
                                            fontSize: '15px',
                                            lineHeight: '24px',
                                            textTransform: 'capitalize',
                                            borderRadius: '999px',
                                            background: reportType === module.key ? 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)' : 'rgba(230, 210, 249, 0.2)',
                                            color: reportType === module.key ? 'white' : 'white',
                                            border: reportType === module.key ? 'none' : '1px solid rgba(230, 210, 249, 0.2)',
                                        }}
                                    >
                                        {module.label}
                                    </Button>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={9}>
                    <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px', padding: '16px !important' }}>
                        <CardContent sx={{ padding: '0 !important' }}>
                            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>{currentLabel} {t('data', 'Data')}</Typography>
                            <Box>
                                <DataGrid
                                    rows={[]}
                                    columns={[
                                        { field: 'date', headerName: t('date', 'Date'), ...(isMobile && { width: 150 }), ...(isLarge && { flex: 1 }), },
                                        { field: 'type', headerName: t('type', 'Type'), ...(isMobile && { width: 150 }), ...(isLarge && { flex: 1 }), },
                                        { field: 'status', headerName: t('status', 'Status'), ...(isMobile && { width: 150 }), ...(isLarge && { flex: 1 }), },
                                        { field: 'amount', headerName: t('amount', 'Amount'), ...(isMobile && { width: 150 }), ...(isLarge && { flex: 1 }), },
                                    ]}
                                    localeText={{
                                        ...(i18n.language?.toLowerCase().startsWith('fr') ? frFR.components.MuiDataGrid.defaultProps.localeText : enUS.components.MuiDataGrid.defaultProps.localeText),
                                        noRowsLabel: t('no_data_found', `No ${currentLabel} data found`, { reportType: currentLabel })
                                    }}
                                    sx={{ 
                                        backgroundColor: 'transparent', 
                                        border: 'none', 
                                        borderRadius: '0', 
                                        '& .MuiDataGrid-toolbarContainer': {
                                        padding: '12px',
                                        margin: '0 -12px',
                                        gap: '10px',
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                        },
                                        '& .MuiDataGrid-topContainer::after': {
                                        display: 'none',
                                        },
                                        '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
                                        color: '#fff',
                                        border: '1px solid rgba(230, 210, 249, 0.2)',
                                        borderRadius: '999px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        padding: '6px 12px',
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                        },
                                        '& .MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-icon .MuiBadge-root svg': {
                                        width: '18px',
                                        height: '18px',
                                        },
                                        '& .MuiDataGrid-virtualScrollerContent': {
                                        maxHeight: 'auto',
                                        height: 'auto !important'
                                        },
                                        '& .MuiDataGrid-virtualScroller': {
                                        borderRadius: '0 !important',
                                        },
                                        '& .MuiDataGrid-virtualScrollerRenderZone': {
                                        position: 'inherit',
                                        },
                                        '& .MuiDataGrid-virtualScrollerRenderZone>.MuiDataGrid-row': {
                                        maxHeight: 'none !important',
                                        height: 'auto !important',
                                        },
                                        '& .MuiDataGrid-cell': {
                                        height: 'auto !important',
                                        minHeight: 'var(--height)',
                                        paddingTop: '5px',
                                        paddingBottom: '5px',
                                        },
                                        '& .MuiDataGrid-cell>.MuiBox-root': {
                                        minHeight: '100%',
                                        alignItems: 'center',
                                        },
                                        '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell': {
                                        borderRadius: '0 !important',
                                        borderColor: 'rgba(230, 210, 249, 0.2) !important',
                                        color: '#fff',
                                        },
                                        '& .MuiDataGrid-columnHeaders [role=row]': {
                                        backgroundColor: '#1a1a1a',
                                        },
                                        '& .MuiDataGrid-footerContainer': {
                                        borderColor: 'rgba(230, 210, 249, 0.2) !important'
                                        },
                                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader': {
                                        color: 'white',
                                        },
                                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                                        outline: 'none',
                                        },
                                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                                        opacity: '0.8 !important',
                                        fill: 'white',
                                        },
                                        '& .MuiDataGrid-cell .MuiChip-outlined': {
                                        border: '0 !important',
                                        height: 'auto',
                                        fontWeight: '600',
                                        },
                                        '& .MuiDataGrid-cell .MuiChip-outlined.MuiChip-colorSuccess': {
                                        color: "#03c11c",
                                        },
                                        '& .MuiTablePagination-root': {
                                        color: '#fff'
                                        },
                                        '& .MuiTablePagination-selectLabel': {
                                        marginBottom: '0',
                                        },
                                        '& .MuiTablePagination-input': {
                                        marginRight: '10px',
                                        },
                                        '& .MuiTablePagination-input .MuiSelect-select': {
                                        paddingTop: '2px',
                                        paddingBottom: '2px',
                                        border: '1px solid rgba(230, 210, 249, 0.2)',
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                        borderRadius: '4px',
                                        },
                                        '& .MuiTablePagination-input .MuiSvgIcon-root': {
                                        fill: 'white'
                                        },
                                        '& .MuiTablePagination-toolbar .MuiTablePagination-displayedRows': {
                                        marginBottom: '0',
                                        },
                                        '& .MuiTablePagination-actions': {
                                        marginLeft: '10px !important',
                                        },
                                        '& .MuiTablePagination-actions .MuiButtonBase-root': {
                                        color: '#FF69B4',
                                        },
                                        '& .MuiDataGrid-filler': {
                                        height: 'auto !important',
                                        },
                                        '& .MuiDataGrid-filler>div': {
                                        borderColor: 'transparent',
                                        },
                                        '& .MuiDataGrid-overlayWrapper': {
                                        height: 'auto !important',
                                        maxHeight: '50px',
                                        },
                                        '& .MuiDataGrid-overlayWrapperInner': {
                                        minHeight: '40px',
                                        height: 'auto !important'
                                        },
                                        '& .MuiDataGrid-overlay': {
                                        color: '#fff',
                                        borderTop: '1px solid rgba(230, 210, 249, 0.2)',
                                        backgroundColor: 'transparent',
                                        minHeight: '40px',
                                        },
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <GlobalStyles
                styles={{
                    '.MuiDataGrid-panel .MuiDataGrid-paper,.MuiPopover-root .MuiPopover-paper, .base-Popper-root .MuiPaper-root, .MuiDialog-root .MuiPaper-root': {
                        backgroundColor: '#252525 !important',
                        border: '1px solid rgba(230, 210, 249, 0.1)',
                        fontSize: '15px',
                        color: '#fff',
                        borderRadius: '4px',
                    },
                    '.MuiDialog-root .MuiPaper-root': {
                        borderRadius: '20px',
                    },
                    '.MuiDataGrid-columnsManagementHeader': {
                        padding: '10px !important',
                    },
                    '.MuiInputBase-formControl': {
                        fontSize: '14px !important',
                        color: 'white !important',
                        borderRadius: '999px !important',
                    },
                    '.MuiInputBase-formControl .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiInputBase-formControl .MuiInputAdornment-positionStart svg': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter': {
                        borderTop: '1px solid rgba(230, 210, 249, 0.1)',
                        padding: '4px 8px 4px 16px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement':{
                        padding: '0px 16px 12px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label,.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiTypography-root': {
                        fontSize: '14px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiCheckbox-root .MuiSvgIcon-root, .MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiSvgIcon-root': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiButtonBase-root': {
                        color: '#FF69B4',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon .MuiSvgIcon-root,.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root svg,.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiFormLabel-root': {
                        color: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:before': {
                        borderColor: 'rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:after': {
                        display: 'none',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root, .base-Popper-root .MuiPaper-root .MuiList-root': {
                        padding: '0',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiTypography-root': {
                        fontSize: '14px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root': {
                        padding: '5px 10px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root.Mui-selected .MuiListItemIcon-root .MuiSvgIcon-root': {
                        fill: '#FF69B4',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiDivider-root': {
                        borderColor: 'rgba(230, 210, 249, 0.3) !important',
                        margin: '0',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        minWidth: '32px',
                    },
                }}
            />
        </Box>
    );
};

export default ReportsPage;


